import { CartLinesUpdateEvent } from '@shopify/events';

async function dispatchCartAdd(detail, lines) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({ action: 'add', context: 'product', lines, promise: deferred.promise })
  );

  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);
    const cart = await response.json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { ...detail, items: cart.items, itemCount: cart.item_count, didError: false },
    });
  } catch (error) {
    deferred.reject(error);
  }
}

// All ship-date math runs in the store's timezone so the window is the same
// for every customer regardless of where they browse from.
const STORE_TIMEZONE = 'America/New_York';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const WEEKDAY_HEADERS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function toISO(year, month, day) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function parseISO(iso) {
  const [year, month, day] = iso.split('-').map(Number);
  return { year, month, day };
}

function isValidISO(iso) {
  return typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(iso);
}

function addDays(iso, days) {
  const { year, month, day } = parseISO(iso);
  const date = new Date(Date.UTC(year, month - 1, day + days));
  return toISO(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}

function weekdayOf(iso) {
  const { year, month, day } = parseISO(iso);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

function daysInMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function todayISO() {
  try {
    // en-CA locale formats as YYYY-MM-DD.
    return new Intl.DateTimeFormat('en-CA', { timeZone: STORE_TIMEZONE }).format(new Date());
  } catch {
    const now = new Date();
    return toISO(now.getFullYear(), now.getMonth() + 1, now.getDate());
  }
}

function nthWeekdayOfMonth(year, month, weekday, n) {
  const firstWeekday = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
  return 1 + ((weekday - firstWeekday + 7) % 7) + (n - 1) * 7;
}

function lastWeekdayOfMonth(year, month, weekday) {
  const total = daysInMonth(year, month);
  const lastDay = new Date(Date.UTC(year, month - 1, total)).getUTCDay();
  return total - ((lastDay - weekday + 7) % 7);
}

// Fixed-date holidays shift to the nearest weekday when they land on a
// weekend (the "observed" date). Block both so neither slips through.
function addWithObserved(dates, year, month, day) {
  const iso = toISO(year, month, day);
  dates.add(iso);
  const weekday = weekdayOf(iso);
  if (weekday === 6) dates.add(addDays(iso, -1));
  if (weekday === 0) dates.add(addDays(iso, 1));
}

// US federal holidays are fully algorithmic, so they renew themselves every
// year without anyone maintaining a date list.
function usFederalHolidays(year) {
  const dates = new Set();
  addWithObserved(dates, year, 1, 1); // New Year's Day
  dates.add(toISO(year, 1, nthWeekdayOfMonth(year, 1, 1, 3))); // MLK Jr. Day
  dates.add(toISO(year, 2, nthWeekdayOfMonth(year, 2, 1, 3))); // Washington's Birthday
  dates.add(toISO(year, 5, lastWeekdayOfMonth(year, 5, 1))); // Memorial Day
  addWithObserved(dates, year, 6, 19); // Juneteenth
  addWithObserved(dates, year, 7, 4); // Independence Day
  dates.add(toISO(year, 9, nthWeekdayOfMonth(year, 9, 1, 1))); // Labor Day
  dates.add(toISO(year, 10, nthWeekdayOfMonth(year, 10, 1, 2))); // Columbus Day
  addWithObserved(dates, year, 11, 11); // Veterans Day
  dates.add(toISO(year, 11, nthWeekdayOfMonth(year, 11, 4, 4))); // Thanksgiving
  addWithObserved(dates, year, 12, 25); // Christmas Day
  return dates;
}

class CartGiftOptions extends HTMLElement {
  #minDate;
  #maxDate;
  #blockedDates;
  #disabledWeekdays;
  #viewYear;
  #viewMonth;

  // Guards against a second purge firing while the first one's cart write is
  // still in flight (the drawer re-renders, and the re-rendered element still
  // carries the not-yet-cleared attribute).
  static #stalePurgeRunning = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('submit', this.#onSubmit);
    this.#purgeStaleShipDate();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('submit', this.#onSubmit);
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const dayButton = event.target.closest('[data-cal-day]');
    if (dayButton instanceof HTMLButtonElement && !dayButton.disabled) {
      this.#selectDate(dayButton.getAttribute('data-cal-day'));
      return;
    }

    const monthNav = event.target.closest('[data-cal-nav]');
    if (monthNav instanceof HTMLButtonElement && !monthNav.disabled) {
      this.#shiftMonth(monthNav.getAttribute('data-cal-nav') === 'next' ? 1 : -1);
      return;
    }

    const addonButton = event.target.closest('[data-cart-addon-variant]');
    if (addonButton instanceof HTMLElement) {
      event.preventDefault();
      const variantId = addonButton.getAttribute('data-cart-addon-variant');
      if (variantId) this.#addVariant(variantId, addonButton);
      return;
    }

    const clearButton = event.target.closest('[data-cart-ship-clear]');
    if (clearButton instanceof HTMLElement) {
      event.preventDefault();
      this.#clearShipDate(clearButton);
      return;
    }

    const shipSummary = event.target.closest('.cart-gift-options__ship-date > summary');
    if (shipSummary instanceof HTMLElement) {
      event.preventDefault();

      const details = shipSummary.closest('.cart-gift-options__ship-date');
      if (details instanceof HTMLDetailsElement) {
        details.open = !details.open;
        if (details.open) this.#initShipDate();
      }
    }
  };

  #onSubmit = (event) => {
    const form = event.target.closest('[data-cart-gift-note-form]');
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();

    const variantId = form.getAttribute('data-variant-id');
    const textarea = form.querySelector('textarea[name="note"]');
    const note = textarea instanceof HTMLTextAreaElement ? textarea.value.trim() : '';

    if (!variantId) return;
    this.#addVariant(variantId, form, note ? { note } : undefined);
  };

  // A cart outlives the date saved in it. Left alone, a shopper coming back in
  // August still sees "Ships July 23rd" in the drawer, and the note that rides
  // into Veeqo still tells the warehouse to hold for a date that's gone. So on
  // load, drop any saved date that's no longer bookable. Deliberately a clear,
  // not a roll-forward: nobody asked us to delay their order to a new date.
  #purgeStaleShipDate() {
    const stored = this.dataset.currentShipDate || '';
    if (!isValidISO(stored) || CartGiftOptions.#stalePurgeRunning) return;

    // Only clear dates that are truly gone (before today, store time). A future
    // date that has merely slipped inside the minimum lead window stays: the
    // shopper picked it deliberately when it was valid, and the Flow hold still
    // honors it.
    if (stored >= todayISO()) return;

    // Do this before awaiting anything. cart-note.js reads this attribute both
    // in its on-load note repair and in its checkout-submit handler, and either
    // would put the stale line back while our cart write is still in flight.
    const visibleNote = document.querySelector('[data-cart-note-visible]');
    if (visibleNote instanceof HTMLTextAreaElement) visibleNote.dataset.shippingDate = '';

    CartGiftOptions.#stalePurgeRunning = true;
    this.#clearShipDate(null, { announce: true }).finally(() => {
      CartGiftOptions.#stalePurgeRunning = false;
    });
  }

  #initShipDate() {
    const container = this.querySelector('[data-ship-calendar]');
    if (!(container instanceof HTMLElement)) return;

    this.#setupShipDateConfig();

    const selected = this.#selectedDate();
    const openTo = selected && this.#inWindow(selected) ? selected : this.#firstAvailableDate();
    const view = parseISO(openTo || this.#minDate);
    this.#viewYear = view.year;
    this.#viewMonth = view.month;

    this.#renderCalendar();
    this.#syncShipDateActions();
  }

  #setupShipDateConfig() {
    const today = todayISO();
    const minDays = Number.parseInt(this.dataset.minDaysAdvance || '3', 10);
    const maxDays = Number.parseInt(this.dataset.daysAdvance || '47', 10);

    this.#minDate = addDays(today, Number.isFinite(minDays) ? minDays : 3);
    this.#maxDate = addDays(today, Number.isFinite(maxDays) ? maxDays : 47);
    this.#disabledWeekdays = this.#disabledWeekdaysList();

    this.#blockedDates = new Set(this.#parseDisabledDates());
    if (this.dataset.blockUsHolidays === 'true') {
      const startYear = parseISO(this.#minDate).year;
      const endYear = parseISO(this.#maxDate).year;
      for (let year = startYear; year <= endYear; year += 1) {
        for (const holiday of usFederalHolidays(year)) this.#blockedDates.add(holiday);
      }
    }
  }

  #selectedDate() {
    const input = this.querySelector('[data-cart-ship-date]');
    const value = input instanceof HTMLInputElement ? input.value : '';
    return isValidISO(value) ? value : '';
  }

  #inWindow(iso) {
    return iso >= this.#minDate && iso <= this.#maxDate;
  }

  #isAvailable(iso) {
    return this.#inWindow(iso) && !this.#blockedDates.has(iso) && !this.#disabledWeekdays.includes(weekdayOf(iso));
  }

  #firstAvailableDate() {
    for (let iso = this.#minDate; iso <= this.#maxDate; iso = addDays(iso, 1)) {
      if (this.#isAvailable(iso)) return iso;
    }
    return '';
  }

  #shiftMonth(offset) {
    let month = this.#viewMonth + offset;
    let year = this.#viewYear;
    if (month < 1) {
      month = 12;
      year -= 1;
    } else if (month > 12) {
      month = 1;
      year += 1;
    }
    this.#viewYear = year;
    this.#viewMonth = month;
    // Defer the rebuild until the click finishes bubbling: replacing the
    // clicked nav button mid-event makes the cart drawer's outside-click
    // check (element.contains(event.target)) fail and close the drawer.
    window.setTimeout(() => this.#renderCalendar(), 0);
  }

  #selectDate(iso) {
    if (!isValidISO(iso) || !this.#isAvailable(iso)) return;

    const input = this.querySelector('[data-cart-ship-date]');
    if (input instanceof HTMLInputElement) input.value = iso;

    for (const day of this.querySelectorAll('[data-cal-day]')) {
      day.setAttribute('aria-pressed', day.getAttribute('data-cal-day') === iso ? 'true' : 'false');
    }

    this.#syncShipDateActions();
    this.#toggleStaleNotice(false);
    // Picking a date IS saving it. A separate save button meant shoppers who
    // forgot to tap it had their order ship immediately.
    this.#autoSaveShipDate(iso);
  }

  #shipDateRequestVersion = 0;
  #shipDateWriteChain = Promise.resolve();

  // Veeqo only imports the order note that exists at checkout (it never
  // re-syncs notes afterward), so the ship date is written into the CART
  // note the moment it's picked. It rides into the order like any customer
  // note, first line, with the customer's own note kept underneath.
  static #SHIP_NOTE_PATTERN = /^Ship Date: \d{4}-\d{2}-\d{2} future\n?/;

  #composeCartNote(existingNote, iso) {
    const rest = (existingNote || '').replace(CartGiftOptions.#SHIP_NOTE_PATTERN, '').replace(/^\n+/, '');
    if (!iso) return rest;
    return `Ship Date: ${iso} future` + (rest ? '\n' + rest : '');
  }

  // The cart drawer does not re-render when we save a ship date, so the note
  // fields must be updated directly. The HIDDEN field is what submits with the
  // cart form at checkout, so it must carry the full note (with the ship line)
  // the instant a date is picked; the visible textarea keeps showing only the
  // customer's own note.
  #reflectNoteToDom(fullNote, iso) {
    const hidden = document.querySelector('[data-cart-note-full]');
    if (hidden instanceof HTMLTextAreaElement) hidden.value = fullNote;

    const visible = document.querySelector('[data-cart-note-visible]');
    if (visible instanceof HTMLTextAreaElement) {
      visible.dataset.shippingDate = iso || '';
      visible.value = this.#composeCartNote(fullNote, null);
    }
  }

  async #fetchCartNote() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return '';
      const cart = await response.json();
      return typeof cart.note === 'string' ? cart.note : '';
    } catch {
      return '';
    }
  }

  async #autoSaveShipDate(iso) {
    const requestVersion = ++this.#shipDateRequestVersion;
    const formatted = this.#formatDate(iso);
    this.#updateSelectedLine(iso, 'saving');

    await this.#enqueueShipDateWrite(async () => {
      if (requestVersion !== this.#shipDateRequestVersion) return false;

      const note = this.#composeCartNote(await this.#fetchCartNote(), iso);
      if (requestVersion !== this.#shipDateRequestVersion) return false;

      const saved = await this.#updateAttributes(
        {
          shipping_date: iso,
          formatted_shipping_date: formatted,
        },
        note
      );

      if (requestVersion !== this.#shipDateRequestVersion) return false;

      if (saved) {
        this.dataset.currentShipDate = iso;
        this.#updateSummary(formatted);
        this.#updateSelectedLine(iso, 'saved');
        this.#reflectNoteToDom(note, iso);
      } else {
        this.#revertShipDateSelection();
      }

      return saved;
    });
  }

  #enqueueShipDateWrite(callback) {
    const run = this.#shipDateWriteChain.catch(() => false).then(callback);
    this.#shipDateWriteChain = run;
    return run;
  }

  #revertShipDateSelection() {
    const current = isValidISO(this.dataset.currentShipDate || '') ? this.dataset.currentShipDate : '';

    const input = this.querySelector('[data-cart-ship-date]');
    if (input instanceof HTMLInputElement) input.value = current;

    for (const day of this.querySelectorAll('[data-cal-day]')) {
      day.setAttribute('aria-pressed', day.getAttribute('data-cal-day') === current ? 'true' : 'false');
    }

    this.#updateSelectedLine(current, current ? 'saved' : undefined);
    this.#syncShipDateActions();
  }

  #renderCalendar() {
    const container = this.querySelector('[data-ship-calendar]');
    if (!(container instanceof HTMLElement)) return;

    const year = this.#viewYear;
    const month = this.#viewMonth;
    const selected = this.#selectedDate();

    const minView = this.#minDate.slice(0, 7);
    const maxView = this.#maxDate.slice(0, 7);
    const currentView = `${year}-${String(month).padStart(2, '0')}`;

    const header = document.createElement('div');
    header.className = 'ship-cal__header';

    const previousButton = document.createElement('button');
    previousButton.type = 'button';
    previousButton.className = 'ship-cal__nav';
    previousButton.setAttribute('data-cal-nav', 'prev');
    previousButton.setAttribute('aria-label', 'Previous month');
    previousButton.disabled = currentView <= minView;
    previousButton.innerHTML = '&lsaquo;';

    const title = document.createElement('span');
    title.className = 'ship-cal__title';
    title.setAttribute('aria-live', 'polite');
    title.textContent = `${MONTH_NAMES[month - 1]} ${year}`;

    const nextButton = document.createElement('button');
    nextButton.type = 'button';
    nextButton.className = 'ship-cal__nav';
    nextButton.setAttribute('data-cal-nav', 'next');
    nextButton.setAttribute('aria-label', 'Next month');
    nextButton.disabled = currentView >= maxView;
    nextButton.innerHTML = '&rsaquo;';

    header.append(previousButton, title, nextButton);

    const weekdays = document.createElement('div');
    weekdays.className = 'ship-cal__weekdays';
    weekdays.setAttribute('aria-hidden', 'true');
    for (const label of WEEKDAY_HEADERS) {
      const cell = document.createElement('span');
      cell.className = 'ship-cal__weekday';
      cell.textContent = label;
      weekdays.append(cell);
    }

    const grid = document.createElement('div');
    grid.className = 'ship-cal__grid';

    const leadingBlanks = weekdayOf(toISO(year, month, 1));
    for (let index = 0; index < leadingBlanks; index += 1) {
      grid.append(document.createElement('span'));
    }

    const total = daysInMonth(year, month);
    for (let day = 1; day <= total; day += 1) {
      const iso = toISO(year, month, day);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ship-cal__day';
      button.textContent = String(day);
      button.setAttribute('data-cal-day', iso);
      button.setAttribute('aria-label', `Ship on ${MONTH_NAMES[month - 1]} ${day}, ${year}`);
      button.setAttribute('aria-pressed', iso === selected ? 'true' : 'false');
      button.disabled = !this.#isAvailable(iso);
      grid.append(button);
    }

    container.replaceChildren(header, weekdays, grid);
    this.#updateSelectedLine(selected, selected ? 'saved' : undefined);
  }

  #updateSelectedLine(iso, state) {
    const line = this.querySelector('[data-ship-date-selected]');
    if (!(line instanceof HTMLElement)) return;

    if (isValidISO(iso)) {
      const formatted = this.#formatDate(iso);
      line.textContent =
        state === 'saving' ? `Saving ${formatted}` : `✓ Saved. Leaves our warehouse ${formatted}`;
      line.classList.toggle('cart-gift-options__selected--saving', state === 'saving');
      line.hidden = false;
    } else {
      line.textContent = '';
      line.hidden = true;
    }
  }

  // Silently dropping the date would leave a gifter who picked one wondering
  // why the row went blank, so say what happened.
  #toggleStaleNotice(visible) {
    const notice = this.querySelector('[data-ship-date-stale]');
    if (notice instanceof HTMLElement) notice.hidden = !visible;
  }

  #updateSummary(formatted) {
    const summary = this.querySelector('[data-ship-date-summary]');
    if (!(summary instanceof HTMLElement)) return;

    const label = summary.getAttribute('data-label') || 'Pick A Ship Date';
    summary.textContent = formatted ? `Ships ${formatted}` : label;
    summary.classList.toggle('cart-gift-options__text--set', Boolean(formatted));
  }

  #disabledWeekdaysList() {
    const map = [
      ['disableSundays', 0],
      ['disableMondays', 1],
      ['disableTuesdays', 2],
      ['disableWednesdays', 3],
      ['disableThursdays', 4],
      ['disableFridays', 5],
      ['disableSaturdays', 6],
    ];

    return map.filter(([key]) => this.dataset[key] === 'true').map(([, day]) => day);
  }

  #parseDisabledDates() {
    const raw = this.dataset.disabledDates || '';

    return raw
      .split(/[\s,]+/)
      .map((date) => date.trim().replace(/^['"]|['"]$/g, ''))
      .filter((date) => isValidISO(date));
  }

  #syncShipDateActions = () => {
    const selected = this.#selectedDate();
    const clearButton = this.querySelector('[data-cart-ship-clear]');

    if (clearButton instanceof HTMLButtonElement) {
      clearButton.disabled = !selected && !this.dataset.currentShipDate;
    }
  };

  async #clearShipDate(trigger, { announce = false } = {}) {
    const requestVersion = ++this.#shipDateRequestVersion;

    const input = this.querySelector('[data-cart-ship-date]');
    if (input instanceof HTMLInputElement) input.value = '';

    let composedNote = '';
    const cleared = await this.#withBusyState(trigger, () =>
      this.#enqueueShipDateWrite(async () => {
        if (requestVersion !== this.#shipDateRequestVersion) return false;

        // Strip the ship-date line from the cart note but keep whatever the
        // customer wrote themselves.
        composedNote = this.#composeCartNote(await this.#fetchCartNote(), null);
        if (requestVersion !== this.#shipDateRequestVersion) return false;

        const saved = await this.#updateAttributes(
          {
            shipping_date: null,
            formatted_shipping_date: null,
          },
          composedNote
        );

        if (requestVersion !== this.#shipDateRequestVersion) return false;

        return saved;
      })
    );

    if (cleared) {
      this.dataset.currentShipDate = '';
      this.#updateSummary('');
      this.#updateSelectedLine('');
      this.#reflectNoteToDom(composedNote, null);
      for (const day of this.querySelectorAll('[data-cal-day]')) {
        day.setAttribute('aria-pressed', 'false');
      }
      this.#toggleStaleNotice(announce);
    } else if (requestVersion === this.#shipDateRequestVersion) {
      this.#revertShipDateSelection();
    }

    this.#syncShipDateActions();
  }

  #formatDate(value) {
    if (!isValidISO(value)) return value;

    const { year, month, day } = parseISO(value);
    return `${MONTH_NAMES[month - 1]} ${day}${this.#daySuffix(day)}, ${year}`;
  }

  #daySuffix(day) {
    if (day > 3 && day < 21) return 'th';

    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  async #updateAttributes(attributes, note) {
    this.#clearError();

    try {
      const payload = note === undefined ? { attributes } : { attributes, note };
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Unable to update cart attributes');

      const cart = await response.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      return true;
    } catch (error) {
      console.error('[cart-gift-options] Failed to update cart attributes', error);
      this.#showError('Sorry, that option could not be saved. Please try again.');
      return false;
    }
  }

  async #addVariant(variantId, trigger, properties) {
    await this.#withBusyState(trigger, async () => {
      // A shopper can submit a gift note while the selected ship date is still
      // saving. Finish that write before adding/morphing the cart, so Shopify
      // cannot apply overlapping cart snapshots and lose the selected date.
      await this.#shipDateWriteChain;
      this.#clearError();

      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
        .filter(Boolean);

      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', '1');

      if (properties) {
        Object.entries(properties).forEach(([key, value]) => {
          body.append(`properties[${key}]`, value);
        });
      }

      if (sectionIds.length) {
        body.append('sections', sectionIds.join(','));
      }

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const data = await response.json();

        if (data.status) {
          this.#showError(data.description || data.message);
          return;
        }

        await dispatchCartAdd(
          {
            source: 'cart-gift-options',
            sourceId: this.id || 'cart-gift-options',
            sections: data.sections,
          },
          [{ merchandiseId: variantId, quantity: 1 }]
        );
      } catch (error) {
        console.error('[cart-gift-options] Failed to add cart option', error);
        this.#showError('Sorry, that option could not be added. Please try again.');
      }
    });
  }

  async #withBusyState(trigger, callback) {
    if (trigger instanceof HTMLElement) trigger.setAttribute('aria-busy', 'true');

    try {
      return await callback();
    } finally {
      if (trigger instanceof HTMLElement) trigger.removeAttribute('aria-busy');
    }
  }

  #showError(message) {
    const region = this.querySelector('[data-cart-gift-options-error]');
    if (region instanceof HTMLElement) {
      region.textContent = message || 'Sorry, that option could not be updated. Please try again.';
      region.hidden = false;
    }
  }

  #clearError() {
    const region = this.querySelector('[data-cart-gift-options-error]');
    if (region instanceof HTMLElement) {
      region.textContent = '';
      region.hidden = true;
    }
  }
}

if (!customElements.get('cart-gift-options')) {
  customElements.define('cart-gift-options', CartGiftOptions);
}
